import React, { useEffect, useState } from 'react';
import { Avatar, Backdrop, Box, Button, Chip, CircularProgress, Grid, Typography } from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Page } from 'components/Page';
import COLORS from 'theme/colors';
import { ROUTE } from 'route/RouteEnums';
import { useAuth } from 'utils/AuthContext';
import { providerPhotoUrl } from 'utils/providerPhoto';
import { toAssignedProviderId } from 'utils/dropDownList';
import { getAppointmentConfirmationDetailsAPI } from 'services/appointmentServices';
import { getPatientDetailsAPI } from 'services/profileServices';
import { cancelSelfSchedulingAppointmentAPI, getSelfSchedulingProvidersAPI } from 'services/selfSchedulingServices';
import { SCHEDULING_UNAVAILABLE_MESSAGE } from 'shared/content/constants';
import { formatProviderName, mapProvider } from 'pages/SelfScheduling/mappers';
import { Provider } from 'pages/SelfScheduling/types';
import CancelAppointmentDialog from './CancelAppointmentDialog';
import {
  canJoinVirtualAppointment,
  isInactiveAppointmentStatus,
  isWithinRescheduleCancelWindow,
  JOIN_WINDOW_MINUTES_BEFORE,
  RESCHEDULE_CANCEL_WINDOW_HOURS,
} from './eligibility';
import { asAppointmentDate, formatAppointmentDay, formatAppointmentTimeRange } from './mappers';

const SectionBox = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      border: `1px solid ${COLORS.Gray300}`,
      borderRadius: '12px',
      backgroundColor: COLORS.White,
      p: 3,
      mb: 3,
    }}
  >
    {children}
  </Box>
);

/**
 * MM-1162: one font style for the appointment box's day, time range, provider name and provider
 * description - the time's style (body1, Graphik-Regular, regular weight) at 16px - so they read
 * as a single group instead of mixing heading and body styles. Colors are set per line.
 */
const APPOINTMENT_DETAIL_TEXT_SX = { fontSize: '16px', fontWeight: 400 };

/**
 * MM-1162: one font size for the body text of the location/virtual box and the "Need to make a
 * change?" box; their headings keep their own variant sizes.
 */
const SECTION_CONTENT_FONT_SIZE = '16px';
/**
 * MM-1162: the provider profile fields shown under the appointment's provider - the same
 * credentials (DrChrono suffix) and description (specialty) the self-scheduling provider cards show.
 */
type ProviderProfile = Pick<Provider, 'credential' | 'description'>;

// Only the fields this page actually needs from AppointmentVO - everything else the dashboard's
// own AppointmentStatus.tsx reads is irrelevant here.
interface ManagedAppointment {
  providerId: number;
  providerName: string;
  consultationType: string;
  officeAddress?: string;
  virtualMeetingLink?: string;
  scheduledTime: string; // yyyy-MM-dd'T'HH:mm:ss, office-local, no offset
  chronoAppointmentId: number;
  officeId: number;
  profileId: number;
  examRoom: number;
  duration: number;
  // Raw DrChrono status (Confirmed/Scheduled/Cancelled/Complete/No-Show/Late Cancel/...) - see
  // eligibility.ts's isInactiveAppointmentStatus for which of these still allow reschedule/cancel.
  status: string;
}

// Small, self-contained color map for the status badge - not the same concern as
// eligibility.ts's active/inactive gate (a Chip needs a color regardless of whether that status
// is active), so kept local to this page rather than shared.
const STATUS_CHIP_COLOR: Record<string, 'info' | 'success' | 'error'> = {
  Confirmed: 'info',
  Scheduled: 'info',
  Complete: 'success',
  'Complete, no follow-up recommended': 'success',
};

const statusChipColor = (status: string): 'info' | 'success' | 'error' => STATUS_CHIP_COLOR[status] ?? 'error';

// The status shown on the appointment's badge - DrChrono sends a blank status for a newly booked
// appointment, which is shown as Scheduled (the same blank-is-active reading
// isInactiveAppointmentStatus already uses).
const displayStatus = (status: string): string => (status.trim() === '' ? 'Scheduled' : status);

const ManageAppointment = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [appointment, setAppointment] = useState<ManagedAppointment | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [canJoinVirtual, setCanJoinVirtual] = useState(false);
  // Whether the patient already has a primary provider assigned (set once their first
  // appointment reaches Completed - see spec/2026-09-11-MM-1067-existing-patient-direct-
  // scheduling.md). Defaults to true (today's only behavior: reschedule stays with the same
  // provider) until resolved or on failure, so an unknown state never grants the broader
  // any-provider directory flow by accident.
  const [hasAssignedProvider, setHasAssignedProvider] = useState(true);
  /**
   * MM-1162: the appointment provider's credentials/description; null until loaded or when the
   * lookup fails, in which case only the provider name is shown (today's behavior).
   */
  const [providerProfile, setProviderProfile] = useState<ProviderProfile | null>(null);

  const fetchAppointment = () => {
    if (!user?.patientId) return;
    setLoading(true);
    getAppointmentConfirmationDetailsAPI(user.patientId)
      .then((response: any) => {
        const data = response?.data;
        if (!data?.chronoAppointmentId || !data?.scheduledTime) {
          setAppointment(null);
          return;
        }
        setAppointment({
          providerId: data.providerId,
          providerName: data.providerName ?? '',
          consultationType: data.consultationType ?? '',
          officeAddress: data.officeAddress,
          virtualMeetingLink: data.virtualMeetingLink,
          scheduledTime: data.scheduledTime,
          chronoAppointmentId: data.chronoAppointmentId,
          officeId: data.officeId,
          profileId: data.profileId,
          examRoom: data.examRoom,
          duration: data.duration,
          status: data.status ?? '',
        });
      })
      .catch(() => {
        toast.error('Unable to load your appointment. Please try again.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAppointment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.patientId]);

  useEffect(() => {
    if (!user?.patientId) return;
    getPatientDetailsAPI(user.patientId)
      // MM-1136: "No assigned provider" is read as null (no assigned provider).
      .then((response: any) => setHasAssignedProvider(!!toAssignedProviderId(response?.data?.providerId)))
      .catch(() => setHasAssignedProvider(true));
  }, [user?.patientId]);

  /**
   * MM-1162: loads the provider's credentials/description from the self-scheduling provider
   * lookup, scoped to this one provider (the same single-provider call the reschedule calendar
   * makes). The lookup also matches on consultation type, so the appointment's own type is sent;
   * payMode is required by the endpoint but only affects the availability preview, which this
   * page ignores.
   */
  useEffect(() => {
    setProviderProfile(null);
    if (!appointment?.providerId) return;
    const consultationType = appointment.consultationType?.toLowerCase().includes('virtual') ? 'Virtual' : 'In-person';
    getSelfSchedulingProvidersAPI({
      patientId: user?.patientId,
      providerId: appointment.providerId,
      consultationType,
      payMode: 'InsurancePay',
    })
      .then((response: any) => {
        const match = (response?.data ?? [])
          .map(mapProvider)
          .find((p: Provider) => String(p.id) === String(appointment.providerId));
        if (match) {
          setProviderProfile({ credential: match.credential, description: match.description });
        }
      })
      .catch(() => setProviderProfile(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointment?.providerId, appointment?.consultationType]);

  useEffect(() => {
    if (!appointment?.scheduledTime) {
      setCanJoinVirtual(false);
      return undefined;
    }
    const checkEligibility = () => {
      setCanJoinVirtual(canJoinVirtualAppointment(asAppointmentDate(appointment.scheduledTime)));
    };
    checkEligibility();
    const interval = setInterval(checkEligibility, 60000);
    return () => clearInterval(interval);
  }, [appointment?.scheduledTime]);

  const handleJoinVirtual = () => {
    if (appointment?.virtualMeetingLink) {
      window.open(appointment.virtualMeetingLink, '_blank', 'noopener,noreferrer');
    } else {
      toast.error('No meeting link is available yet for this appointment.');
    }
  };

  const handleReschedule = () => {
    if (!appointment) return;
    const { startTime, endTime } = formatAppointmentTimeRange(appointment.scheduledTime, appointment.duration);
    const reschedule = {
      appointmentId: appointment.chronoAppointmentId,
      providerName: appointment.providerName,
      currentSlot: {
        scheduledTime: appointment.scheduledTime,
        examRoom: appointment.examRoom,
        duration: appointment.duration,
        profileId: appointment.profileId,
        officeId: appointment.officeId,
        patientId: user?.patientId,
        providerId: appointment.providerId,
      },
      currentSummary: {
        day: formatAppointmentDay(appointment.scheduledTime),
        time: `${startTime} - ${endTime}`,
        officeAddress: appointment.officeAddress,
      },
    };
    // An existing patient (assigned provider) stays locked to that provider's own calendar,
    // same as today; a new patient (no assigned provider yet, not established with anyone) can
    // instead replace their appointment with any provider/office from the full directory - see
    // spec/2026-09-18-MM-1067-new-patient-cross-provider-reschedule.md.
    if (hasAssignedProvider) {
      navigate(ROUTE.SELF_SCHEDULING_PROVIDER.replace(':providerId', String(appointment.providerId)), {
        state: { reschedule },
      });
    } else {
      navigate(ROUTE.SELF_SCHEDULING, { state: { reschedule } });
    }
  };

  const handleCancelClick = () => setCancelDialogOpen(true);
  const handleKeepAppointment = () => setCancelDialogOpen(false);
  const handleConfirmCancel = () => {
    if (!appointment) return;
    setCancelLoading(true);
    cancelSelfSchedulingAppointmentAPI(appointment.chronoAppointmentId, {
      patientId: user?.patientId,
      doctor: appointment.providerId,
      office: appointment.officeId,
      profileId: appointment.profileId,
      examRoom: appointment.examRoom,
      duration: appointment.duration,
      scheduledTime: appointment.scheduledTime,
    })
      .then((response: any) => {
        if (response?.data?.apiResult) {
          setCancelDialogOpen(false);
          toast.success('Appointment cancelled.');
          navigate(ROUTE.DASHBOARD);
        } else if (response?.data?.throttled) {
          toast.error(SCHEDULING_UNAVAILABLE_MESSAGE);
        } else {
          toast.error(response?.data?.messsage ?? 'Failed to cancel appointment. Please try again.');
        }
      })
      .catch(() => {
        toast.error('Failed to cancel appointment. Please try again.');
      })
      .finally(() => setCancelLoading(false));
  };

  if (loading) {
    return (
      <Page title='Manage Appointment'>
        <Box sx={{ textAlign: 'center', p: 6 }}>
          <CircularProgress size={32} />
        </Box>
      </Page>
    );
  }

  if (!appointment) {
    return (
      <Page title='Manage Appointment'>
        <SectionBox>
          <Typography variant='body1' sx={{ color: COLORS.Gray600, textAlign: 'center' }}>
            You have no upcoming appointment to manage.
          </Typography>
        </SectionBox>
      </Page>
    );
  }

  const isVirtual = appointment.consultationType?.toLowerCase().includes('virtual');
  const isInactive = isInactiveAppointmentStatus(appointment.status);
  const withinWindow = isWithinRescheduleCancelWindow(asAppointmentDate(appointment.scheduledTime));
  const day = formatAppointmentDay(appointment.scheduledTime);
  const { startTime, endTime } = formatAppointmentTimeRange(appointment.scheduledTime, appointment.duration);

  // An inactive appointment shows neither this nor the Reschedule/Cancel buttons below (see
  // isInactive's own doc) - null renders nothing rather than an empty SectionBox.
  let manageActionContent = null;
  if (!isInactive && withinWindow) {
    manageActionContent = (
      <SectionBox>
        <Typography variant='h4' sx={{ fontWeight: 600, color: COLORS.Gray900, mb: 1 }}>
          Need to make a change?
        </Typography>
        {/**
         * MM-1152: points the patient to Secure Chat (preferred) or the front office phone, since
         * reschedule/cancel isn't available in the app inside the window.
         */}
        <Typography variant='body2' sx={{ color: COLORS.Gray600, fontSize: SECTION_CONTENT_FONT_SIZE }}>
          Appointments cannot be rescheduled or canceled through the app within {RESCHEDULE_CANCEL_WINDOW_HOURS} hours
          of your appointment. For assistance, contact our front office through Secure Chat (preferred) or call
          618-310-0085.
        </Typography>
      </SectionBox>
    );
  } else if (!isInactive) {
    manageActionContent = (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: { xs: 1.5, sm: '20px' },
        }}
      >
        <Button
          variant='contained'
          onClick={handleReschedule}
          sx={{ textTransform: 'none', minWidth: { xs: '140px', sm: '200px' } }}
        >
          Reschedule
        </Button>
        <Button
          variant='outlined'
          onClick={handleCancelClick}
          sx={{ textTransform: 'none', minWidth: { xs: '140px', sm: '200px' } }}
        >
          Cancel
        </Button>
      </Box>
    );
  }

  return (
    <Page title='Manage Appointment'>
      {/* Above theme.zIndex.modal (StyledDialog/CancelAppointmentDialog's own zIndex, 1300 by
          default), not just theme.zIndex.drawer (1200) - CancelAppointmentDialog stays open and
          visible while cancelling, so this must render in front of it, not behind it. */}
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }} open={cancelLoading}>
        <CircularProgress color='inherit' />
      </Backdrop>

      <SectionBox>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Typography variant='h3' sx={{ color: COLORS.Gray900 }}>
            Appointment
          </Typography>
          {/* Whenever an appointment is shown at all, its status badge must show too - status
              falls back to 'Scheduled' (matching isInactiveAppointmentStatus('') already
              treating a blank status as active) rather than silently rendering no badge when
              the backend's status string is blank/missing. */}
          <Chip
            label={displayStatus(appointment.status)}
            color={statusChipColor(displayStatus(appointment.status))}
            size='small'
          />
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <CalendarMonthOutlinedIcon sx={{ color: COLORS.DarkBlue, mt: 0.5 }} />
              <Box>
                <Typography variant='body1' sx={{ ...APPOINTMENT_DETAIL_TEXT_SX, color: COLORS.Gray900 }}>
                  {day}
                </Typography>
                <Typography variant='body1' sx={{ ...APPOINTMENT_DETAIL_TEXT_SX, color: COLORS.Gray600 }}>
                  {startTime} &ndash; {endTime}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={providerPhotoUrl(appointment.providerId)}
                alt={appointment.providerName}
                sx={{ width: 72, height: 72, backgroundColor: COLORS.LightBlue, color: COLORS.DarkBlue }}
              >
                <PersonIcon sx={{ fontSize: 34 }} />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant='body1'
                  sx={{
                    ...APPOINTMENT_DETAIL_TEXT_SX,
                    fontWeight: 600,
                    color: COLORS.Gray900,
                    wordBreak: 'break-word',
                  }}
                >
                  {/* MM-1162: "Name, Credentials" in bold, matching the self-scheduling provider cards. */}
                  {formatProviderName({
                    name: appointment.providerName,
                    credential: providerProfile?.credential ?? '',
                  })}
                </Typography>
                {providerProfile?.description ? (
                  <Typography
                    variant='body1'
                    sx={{ ...APPOINTMENT_DETAIL_TEXT_SX, color: COLORS.Gray600, wordBreak: 'break-word' }}
                  >
                    {providerProfile.description}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </SectionBox>

      <SectionBox>
        {!isVirtual ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <LocationOnOutlinedIcon sx={{ color: COLORS.DarkBlue }} />
              <Typography variant='h3' sx={{ color: COLORS.Gray900 }}>
                In-person Appointment Location:
              </Typography>
            </Box>
            <Typography variant='body2' sx={{ color: COLORS.Gray600, fontSize: SECTION_CONTENT_FONT_SIZE }}>
              {appointment.officeAddress ?? 'Location details unavailable.'}
            </Typography>
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <VideocamOutlinedIcon sx={{ color: COLORS.DarkBlue }} />
              <Typography variant='h3' sx={{ color: COLORS.Gray900 }}>
                Virtual Appointment
              </Typography>
            </Box>
            {/**
             * MM-1152: a single line naming Zoom and when the join link appears; the join button
             * below shows once the appointment is inside the join window.
             */}
            <Typography
              variant='body2'
              sx={{ color: COLORS.Gray600, fontSize: SECTION_CONTENT_FONT_SIZE, mb: canJoinVirtual ? 2 : 0 }}
            >
              Your appointment will be held on Zoom. The link to join will be available here{' '}
              {JOIN_WINDOW_MINUTES_BEFORE} minutes before your appointment.
            </Typography>
            {canJoinVirtual && (
              <Button variant='contained' onClick={handleJoinVirtual} sx={{ textTransform: 'none' }}>
                Click here to join
              </Button>
            )}
          </>
        )}
      </SectionBox>

      {manageActionContent}

      <CancelAppointmentDialog
        open={cancelDialogOpen}
        onKeep={handleKeepAppointment}
        onConfirmCancel={handleConfirmCancel}
      />
    </Page>
  );
};

export default ManageAppointment;
